import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Monitor, Smartphone, Globe, Clock, Shield, LogOut, Loader2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/api';

function timeAgo(isoString) {
  if (!isoString) return 'Unknown';
  const now = new Date();
  const then = new Date(isoString);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}

function formatLocation(city, country) {
  if (city && country) return `${city}, ${country}`;
  if (country) return country;
  if (city) return city;
  return '';
}

const LoginSessionsSection = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await apiClient.get('/auth/sessions');
      setSessions(res.data.sessions || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const revokeSession = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await apiClient.delete(`/auth/sessions/${sessionId}`);
      setSessions(prev => prev.map(s => s.session_id === sessionId ? { ...s, is_active: false } : s));
      toast.success('Session revoked');
    } catch {
      toast.error('Failed to revoke session');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6" data-testid="sessions-loading">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Login Activity</h3>
        </div>
        <div className="h-24 flex items-center justify-center text-gray-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading sessions...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6" data-testid="login-sessions-section">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Login Activity</h3>
        </div>
        <span className="text-xs text-gray-400">{sessions.filter(s => s.is_active).length} active</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">Recent devices that signed into your account.</p>

      {sessions.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center" data-testid="no-sessions">No login activity recorded yet.</p>
      ) : (
        <>
          <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1" data-testid="sessions-list">
            {sessions.map((s) => {
              const location = formatLocation(s.city, s.country);
              return (
                <div
                  key={s.session_id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    s.is_current
                      ? 'bg-emerald-50 border-emerald-200'
                      : s.is_active
                      ? 'bg-white border-gray-200'
                      : 'bg-gray-50 border-gray-100 opacity-60'
                  }`}
                  data-testid={`session-row-${s.session_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                      s.is_current ? 'bg-emerald-100 text-emerald-700' : s.is_active ? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {s.is_mobile ? <Smartphone className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800 truncate">{s.browser || 'Unknown browser'} on {s.os || 'Unknown OS'}</span>
                        {s.is_current && (
                          <span className="text-[10px] font-semibold bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid="current-session-badge">
                            THIS DEVICE
                          </span>
                        )}
                        {!s.is_active && (
                          <span className="text-[10px] font-medium bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">SIGNED OUT</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        {location && (
                          <span className="flex items-center gap-1" data-testid={`session-location-${s.session_id}`}>
                            <MapPin className="w-3 h-3" />{location}
                          </span>
                        )}
                        <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{s.ip_address || '--'}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(s.login_time)}</span>
                      </div>
                    </div>
                  </div>

                  {s.is_active && !s.is_current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-shrink-0 text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                      onClick={() => revokeSession(s.session_id)}
                      disabled={revokingId === s.session_id}
                      data-testid={`revoke-session-${s.session_id}`}
                    >
                      {revokingId === s.session_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-3 italic" data-testid="location-disclaimer">
            Location is approximate based on IP address.
          </p>
        </>
      )}
    </div>
  );
};

export default LoginSessionsSection;
